import { paintIconAction } from './82-icon-actions.mjs';
import { uiText } from '../04-i18n.mjs';
import { createGemPlayback, createGemGuide } from './39-gem-playback.mjs';
import { createPipePlayback } from './56-pipe-playback.mjs';
import { createNumberPlayback } from './52-number-playback.mjs';
import { createClusterPlayback } from './53-cluster-playback.mjs';
import { createBlockPlayback } from './44-block-playback.mjs';
import { createClusterHud } from './183-cluster-presentation.mjs';
import { createClusterGuide } from './184-cluster-guide.mjs';
import { createScoreArcadeHud, createScoreArcadeGuide } from './189-score-arcade.mjs';

/** Choose one cancellable presentation owner for the canonical native board. */
export function createBoardPlayback(ctx, board, playfield, gameId, options) {
  if (gameId === 'rotation-loop') return createPipePlayback(ctx, board, options);
  if (['soft-merge', 'tile-slide'].includes(gameId))
    return createNumberPlayback(ctx, board, options);
  if (gameId === 'cluster-pop') return createClusterPlayback(ctx, board, options);
  if (gameId === 'block-fit') return createBlockPlayback(ctx, board, options);
  return createGemPlayback(ctx, board, playfield, options);
}
/** Canonical game card markup, shared by finite practice and timed arcade runs.
 * Handlers remain owned by the card controller; this module owns no game state. */
export function createGameChrome(
  ctx,
  {
    game,
    board,
    host,
    heading,
    action,
    start,
    togglePause,
    resetRound,
    undoMove,
    finishRound,
    showScores,
    paint,
  },
) {
  const playfield = ctx.e('div', 'game-playfield');
  const scene = ctx.e('div', 'game-scene');
  scene.setAttribute('aria-hidden', 'true');
  playfield.append(scene, board);
  const options = ctx.e('div', 'game-options');
  const progress = ctx.e('p', 'game-progress');
  const feedback = ctx.e('span', 'game-score-feedback');
  feedback.setAttribute('role', 'status');
  feedback.setAttribute('aria-live', 'polite');
  const arcade = ctx.e('div', 'game-arcade-hud');
  const roundLabel = ctx.e('span', 'game-round-label');
  const runBest = ctx.e('span', 'game-run-best');
  arcade.append(roundLabel, runBest);
  const paintArcade =
    game.id === 'gem-swap'
      ? createGemArcadeHud(ctx, arcade, roundLabel, runBest)
      : game.id === 'cluster-pop'
        ? createClusterHud(ctx, arcade, roundLabel, runBest)
        : createScoreArcadeHud(ctx, game.id, arcade, roundLabel, runBest) || (() => {});
  arcade.hidden = game.kind !== 'score';
  const celebration = ctx.e('div', 'game-celebration');
  celebration.setAttribute('aria-hidden', 'true');
  celebration.hidden = true;
  playfield.append(celebration);
  const hint = ctx.e('p', 'game-hint', game.description);
  const status = ctx.e('p', 'game-status');
  status.setAttribute('role', 'status');
  const meter = ctx.e('div', 'game-time-track');
  const fill = ctx.e('span');
  meter.append(fill);
  meter.setAttribute('aria-hidden', 'true');
  const tally = ctx.e('span', 'game-tally');
  const controls = ctx.e('footer', 'game-controls');
  const play = action('2:00', 'Start a two-minute round', start, 'play');
  const pause = action('', 'Pause this round', togglePause, 'pause');
  const reset = action('', 'Start a fresh game', resetRound, 'refresh-cw');
  const undo = action('', 'Undo the last action', undoMove, 'undo-2');
  const finish = action('Finish', 'Finish this round', finishRound);
  const scores = action('Scores', 'View published scores', showScores, 'trophy');
  const help = action(
    '',
    'How to play',
    () => {
      if (guide) {
        more.open = false;
        if (host.dataset.playing === 'true') togglePause();
        guide.show();
      }
      hint.dataset.help = String(hint.dataset.help !== 'true');
      paint();
    },
    'info',
  );
  const guide =
    game.id === 'gem-swap'
      ? createGemGuide(ctx, {
          playfield,
          board,
          help,
        })
      : game.id === 'cluster-pop'
        ? createClusterGuide(ctx, {
            playfield,
            board,
            auto: host.dataset.immersive === 'true',
          })
        : createScoreArcadeGuide(ctx, {
            game,
            playfield,
            board,
            help,
            auto: host.dataset.immersive === 'true',
          });
  const more = ctx.e('details', 'game-more');
  const summary = ctx.e('summary', '', 'Game options');
  paintIconAction(ctx, summary, 'ellipsis', 'Game options');
  const moreActions = ctx.e('div', 'game-more-actions');
  moreActions.append(reset, finish, scores, help);
  more.append(summary, moreActions);
  controls.append(tally, play, pause, undo, more);
  if (game.id === 'cluster-pop') {
    paintIconAction(ctx, scores, 'trophy', 'View published scores');
    controls.insertBefore(scores, more);
  }
  if (game.id === 'gem-swap') {
    // Expansion and its close action stay visible, including inside the dialog.
    // Sharing remains occasional; moving the live card never recreates its board.
    for (const control of Array.from(heading.children).slice(1)) {
      if (control.classList.contains('game-expand')) heading.append(arcade, control);
      else moreActions.append(control);
    }
    paintIconAction(ctx, play, 'play', 'Start a two-minute round');
    controls.insertBefore(reset, more);
    controls.insertBefore(help, more);
    heading.dataset.fruitHud = 'true';
  }
  host.append(
    heading,
    meter,
    hint,
    ...(game.id === 'gem-swap' ? [] : [arcade]),
    progress,
    playfield,
    options,
    feedback,
    status,
    controls,
  );
  return {
    playfield,
    scene,
    options,
    progress,
    feedback,
    arcade,
    roundLabel,
    runBest,
    celebration,
    hint,
    status,
    meter,
    fill,
    tally,
    controls,
    play,
    pause,
    reset,
    undo,
    finish,
    scores,
    more,
    moreActions,
    paintArcade,
    guide,
    paintRoundControls(round) {
      paintGameRoundControls(
        ctx,
        {
          game,
          host,
          progress,
          roundLabel,
          runBest,
          hint,
          play,
          pause,
          finish,
          undo,
          reset,
          scores,
          meter,
          tally,
        },
        round,
      );
    },
  };
}

/** Project round state onto persistent controls without owning a second clock,
 * game state, permission decision or focused-button replacement. */
export function paintGameRoundControls(ctx, nodes, round) {
  const {
    game,
    host,
    progress,
    roundLabel,
    runBest,
    hint,
    play,
    pause,
    finish,
    undo,
    reset,
    scores,
    meter,
    tally,
  } = nodes;
  const {
    state,
    view,
    world,
    best,
    started,
    practice,
    paused,
    done,
    busy,
    resolving,
    allowed,
    pendingSubmission,
    activeMs,
    historyLength,
    gemHint,
  } = round;
  progress.textContent = view.progress?.total
    ? `${view.progress.label} · ${view.progress.current}/${view.progress.total}`
    : view.progress?.label || '';
  roundLabel.textContent = `LEVEL ${view.stage || 1}${view.combo > 1 ? ` · CHAIN ${view.combo}` : ''}`;
  runBest.textContent = best ? `SESSION BEST ${best}` : 'MAKE YOUR FIRST MOVE';
  hint.textContent =
    hint.dataset.help === 'true'
      ? view.instructions || game.instructions
      : view.goal || game.description;
  host.dataset.playing = String(started && !paused && !done);
  host.dataset.complete = String(state.completed || done);
  host.dataset.gameLevel = String(view.stage || 1);
  host.dataset.gameWorld = String(world);
  play.hidden = game.kind === 'idle' || (started && !practice && !done);
  play.disabled = busy || resolving || !allowed;
  if (gemHint) gemHint.disabled = busy || resolving || paused || done || !allowed;
  pause.hidden = !started || game.kind === 'idle' || done || (practice && !paused);
  paintPauseAction(ctx, pause, paused);
  finish.hidden = !started || (done && !pendingSubmission) || game.kind === 'idle';
  paintFinishAction(finish, pendingSubmission, !practice && activeMs < 120000, ctx);
  finish.disabled = busy || !allowed;
  undo.hidden = game.kind !== 'idle';
  undo.disabled = !historyLength || busy;
  reset.disabled = busy;
  scores.hidden = game.kind === 'idle';
  meter.hidden = game.kind === 'idle' || practice || !started;
  tally.textContent =
    game.kind === 'idle'
      ? state.completed
        ? 'Lovely. All done.'
        : 'No timer'
      : practice
        ? `${state.score} points · No timer`
        : `${state.score} points`;
}

function createGemArcadeHud(ctx, arcade, level, score) {
  const moves = ctx.e('span', 'game-gem-moves');
  arcade.append(moves);
  const chip = (host, icon, number, label) => {
    host.dataset.value = String(number);
    host.setAttribute('aria-label', label);
    host.title = label;
    host.replaceChildren(ctx.e('strong', '', String(number)));
    if (ctx.icon) host.insertBefore(ctx.icon(icon), host.firstChild);
  };
  return (state, view, best) => {
    chip(level, 'flag', view.stage || 1, `Level ${view.stage || 1}`);
    chip(
      score,
      'star',
      state.score.toLocaleString(),
      `${state.score} points · Session best ${best}`,
    );
    const remaining = Math.max(0, (view.progress?.total || 20) - (view.progress?.current || 0));
    chip(moves, 'move-horizontal', remaining, `${remaining} productive swaps remaining`);
  };
}

/** Keep explicit practice continuation focused on a stable, actionable control. */
export function focusGameControl(gameId, board, options, tools, toolsSummary) {
  const target =
    gameId === 'soft-merge'
      ? board
      : gameId === 'thread-loom'
        ? toolsSummary
        : [...board.children].find((cell) => cell.dataset.cell !== undefined && !cell.disabled) ||
          [...options.children].find(
            (option) => option.dataset.option !== undefined && !option.disabled,
          ) ||
          ([...options.children].includes(tools) ? toolsSummary : null);
  target?.focus?.({ preventScroll: true });
}

/** Describe accepted playback events without presenting unearned zero-point awards. */
export function playbackFeedback(event) {
  if (event.type === 'shuffle')
    return uiText(
      event.reason.startsWith('no swaps')
        ? 'No legal moves left · Refilling · Earned powers kept'
        : 'Fresh board · Refilling · Earned powers kept',
    );
  if (event.type === 'invalid') return uiText('Try another swap · Match three in a row');
  const label =
    event.label ||
    (event.chain
      ? `Cascade ${event.chain}`
      : event.lines
        ? `${event.lines} lines cleared`
        : event.type === 'merge'
          ? `${event.cells.length} merges`
          : `${event.cells.length} bubbles popped`);
  return [event.points > 0 ? `+${event.points}` : null, label].filter(Boolean).join(' · ');
}

/** Keep every arcade identity and sharing control in the same heading structure. */
export function createGameShell(ctx, game, action, share) {
  const host = ctx.e('article', 'game-card');
  host.dataset.gameId = game.id;
  host.dataset.gameKind = game.kind;
  host.dataset.contentKind = 'game';
  host.dataset.tags = game.tags.join(' ');
  const sharing = action('', 'Share this game', share, 'share-2');
  const heading = ctx.e('header', 'game-heading');
  const eyebrow = ctx.e(
    'span',
    'game-eyebrow',
    game.kind === 'idle' ? 'A LITTLE PLAY' : 'ARCADE · PRIVATE PLAY',
  );
  heading.append(ctx.append(ctx.e('div'), eyebrow, ctx.e('h2', '', game.title)), sharing);
  return { host, heading };
}

/** Ending a timed run early is explicitly private; only deadline scores submit. */
export function paintFinishAction(button, pending, early, ctx) {
  const text = pending ? 'Retry score' : early ? 'End early' : 'Finish';
  const label = pending
    ? 'Retry recording this score'
    : early
      ? 'End early without recording a score'
      : 'Finish this round';
  paintIconAction(ctx, button, pending ? 'refresh-cw' : 'check', label, text);
}
/** Explain a newly started round's recording choice without changing consent. */
export function gameRoundStatus(session, anonymous) {
  if (!session) return 'Private round · no score record';
  return (
    (session.board?.label ? session.board.label + ' · ' : '') +
    (anonymous ? 'Scores publish anonymously.' : 'Scores publish with your public name.')
  );
}

/** Update the current pause action without replacing the focused control. */
export function paintPauseAction(ctx, button, paused) {
  paintIconAction(
    ctx,
    button,
    paused ? 'play' : 'pause',
    paused ? 'Resume this round' : 'Pause this round',
  );
}
